import type { CurriculumLink } from '../types';

/**
 * The two curriculum slots on a class must not hold the same course.
 *
 * `curriculumLink2` exists for a SPLIT or COMBINED class — one room teaching two courses, so the
 * picker shows both sets of expectations side by side. Pointing both slots at the same course is
 * never what anyone means, and it is not harmless: every expectation then appears twice in the
 * picker and twice in the Assessment Lab's targeting list, so a teacher tagging an assessment
 * cannot tell the duplicates apart.
 *
 * It is live data, not a hypothesis. Two "Junior Drama" classes in production carry
 * `ON-arts-9-10-2010.json` in both slots.
 *
 * A pure comparison so the UI can warn before the click and the writer can refuse after it.
 * Identity is FILENAME PLUS COURSE CODE: the same document legitimately fills both slots when
 * the two courses differ — a split class teaching ENG3U and ENG4U is one Arts/English document
 * and two real courses, which must keep working.
 */
export const sameCurriculumCourse = (
  a: CurriculumLink | null | undefined,
  b: CurriculumLink | null | undefined,
): boolean => {
  if (!a || !b) return false;
  const norm = (s: string | undefined) => (s || '').trim().toLowerCase();
  return norm(a.filename) === norm(b.filename) && norm(a.courseCode) === norm(b.courseCode);
};

/** The message both the UI warning and the writer's refusal use, so they cannot disagree. */
export const DUPLICATE_LINK_MESSAGE =
  'That course is already linked in the other slot. The second slot is for a split or combined '
  + 'class teaching a DIFFERENT course — linking the same one twice shows every expectation twice.';
